import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import dayjs from 'dayjs';
import { getPassportData } from 'features/newsales/newsalesActions';
import {
  selectPassportData,
  selectPassportDataLoading,
} from 'features/newsales/newsalesSlice';
import * as yup from 'yup';

import { useLeadForm } from '../LeadFormContext';
import {
  Container,
  InputGroup,
  InputWrapper,
  Label,
  MainInfoContent,
  Title,
} from './PassportInfo.styles';

const yesterday = dayjs().subtract(1, 'day');
const tenYearsAgo = dayjs().subtract(10, 'year').add(1, 'day');

const schema = yup.object({
  passportId: yup.string().notRequired(),
  passportAuthority: yup
    .string()
    .notRequired()
    .test(
      'digits-only',
      'Passport Authority must contain digits only',
      (value) => !value || /^\d+$/.test(value)
    ),
  passportGivenDate: yup.string().nullable().notRequired(),
  dateOfBirth: yup.string().nullable().notRequired(),
  registeredAddress: yup.string().notRequired(),
});

const PassportInfo = ({ onValidationChange }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const passportData = useSelector(selectPassportData);
  const isLoadingPassport = useSelector(selectPassportDataLoading);
  const { formData, updateFormData } = useLeadForm();

  const leadId = searchParams.get('leadId');
  const isEditMode = searchParams.get('editMode') === 'true';

  const { control, reset, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      passportId: formData.passportId || '',
      passportAuthority: formData.passportAuthority || '',
      passportGivenDate: formData.passportGivenDate || '',
      dateOfBirth: formData.dateOfBirth || '',
      registeredAddress: formData.registeredAddress || '',
    },
  });

  const formValues = watch();

  useEffect(() => {
    if (isEditMode && leadId) {
      dispatch(getPassportData(leadId));
    }
  }, [isEditMode, leadId]);

  useEffect(() => {
    if (passportData) {
      reset({
        passportId: passportData.passportId || '',
        passportAuthority: passportData.passportAuthority || '',
        passportGivenDate: passportData.passportGivenDate || '',
        dateOfBirth: passportData.dateOfBirth || '',
        registeredAddress: passportData.registeredAddress || '',
      });
    }
  }, [passportData]);

  useEffect(() => {
    updateFormData({
      passportId: formValues.passportId || '',
      passportAuthority: formValues.passportAuthority || '',
      passportGivenDate: formValues.passportGivenDate || '',
      dateOfBirth: formValues.dateOfBirth || '',
      registeredAddress: formValues.registeredAddress || '',
    });
  }, [
    formValues.passportId,
    formValues.passportAuthority,
    formValues.passportGivenDate,
    formValues.dateOfBirth,
    formValues.registeredAddress,
  ]);

  useEffect(() => {
    onValidationChange?.(true);
  }, []);

  if (isLoadingPassport) return null;

  return (
    <Container>
      <Title>Passport Data</Title>
      <MainInfoContent>
        <InputGroup className="margin-none">
          <InputWrapper className="name-input">
            <Label>Passport ID</Label>
            <Controller
              control={control}
              name="passportId"
              render={({ field }) => <Input {...field} placeholder="Passport ID" fullWidth />}
            />
          </InputWrapper>
          <InputWrapper className="name-input">
            <Label>Passport Authority</Label>
            <Controller
              control={control}
              name="passportAuthority"
              render={({ field }) => <Input {...field} placeholder="Digits only" fullWidth />}
            />
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="date-input">
            <Label>Passport Given Date</Label>
            <Controller
              control={control}
              name="passportGivenDate"
              render={({ field }) => (
                <CustomDatePicker
                  value={field.value}
                  onChange={field.onChange}
                  maxDate={yesterday}
                  minDate={tenYearsAgo}
                  hideActionBar
                  height={55}
                  showClearIcon={false}
                />
              )}
            />
          </InputWrapper>
          <InputWrapper className="date-input">
            <Label>Date of Birth</Label>
            <Controller
              control={control}
              name="dateOfBirth"
              render={({ field }) => (
                <CustomDatePicker
                  value={field.value}
                  onChange={field.onChange}
                  maxDate={yesterday}
                  hideActionBar
                  height={55}
                  showClearIcon={false}
                />
              )}
            />
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="name-input">
            <Label>Registered Address</Label>
            <Controller
              control={control}
              name="registeredAddress"
              render={({ field }) => (
                <Input {...field} placeholder="Registered Address" fullWidth />
              )}
            />
          </InputWrapper>
        </InputGroup>
      </MainInfoContent>
    </Container>
  );
};

export default PassportInfo;
